import { Fragment, useEffect, useState, useRef } from "react"
import { Switch, Route, Redirect, useLocation, useHistory } from "react-router-dom"

import * as Sentry from "@sentry/react"
import { Amplify, Hub, Auth } from "aws-amplify"

import "@progress/kendo-theme-material/dist/all.css"
import "./Styles/Main.scss"
import "./Styles/Custom.scss"

import { useTheme } from "@material-ui/core/styles"
import useMediaQuery from "@material-ui/core/useMediaQuery"
import Container from "@material-ui/core/Container"
import Box from "@material-ui/core/Box"
import Grid from "@material-ui/core/Grid"

import { Ripple } from "@progress/kendo-react-ripple"
import { Fade } from "@progress/kendo-react-animation"
import { Notification, NotificationGroup } from "@progress/kendo-react-notification"
import { AppBar, AppBarSection, Avatar } from "@progress/kendo-react-layout"
import { Button } from "@progress/kendo-react-buttons"
import { ProgressBar } from "@progress/kendo-react-progressbars"
import { Popup } from "@progress/kendo-react-popup"

import ConfirmDialog from "./Shared/ConfirmDialog/ComfirmDialog"
import MessageDialog from "./Shared/MessageDialog/MessageDialog"
import DrawerContainer from "./Shared/DrawerContainer/DrawerContainer"
import NotFound from "./Routes/NotFound/NotFound"
import Debug from "./Routes/Debug/Debug"
import Feedback from "./Routes/Feedback/Feedback"
import ErrorFallback from "./Routes/ErrorFallback/ErrorFallback"
import MaintenanceFallback from "./Routes/MaintenanceFallback/MaintenanceFallback"
import Home from "./Routes/Home/Home"
import GoogleSignOn from "./Shared/GoogleSignOn/GoogleSignOn"
import { log } from "./Shared/Services/LogService"
import ManageUsers from "./Shared/ManageUsers/ManageUsers"
import HolbaRegistration from "./Routes/Registration/HolbaRegistration"
import NwecRegistration from "./Routes/Registration/NwecRegistration"
import SendRequest from "./Routes/Registration/SendRequests/SendRequest"
import NominateOfficers from './Routes/NominateOfficers/NominateOfficers'

const notificationSeverityTypes = ["info", "warning", "error", "success", "none"]

function App() {
  const materialUiTheme = useTheme()
  const sm = useMediaQuery(materialUiTheme.breakpoints.down("sm"))

  const location = useLocation()
  const history = useHistory()
  const query = new URLSearchParams(useLocation().search)

  const authPopupAnchor = useRef(null)

  const notificationDefaultState = {
    pack: [],
    open: false,
    message: "",
  }
  const [notification, setNotification] = useState(notificationDefaultState)

  const confirmDialogDefaultState = {
    open: false,
    title: "",
    content: "",
    onConfirm: () => {},
    closeOnConfirm: true,
  }
  const [confirmDialog, setConfirmDialog] = useState(confirmDialogDefaultState)

  const messageDialogDefaultState = {
    open: false,
    title: "",
    content: "",
  }
  const [messageDialog, setMessageDialog] = useState(messageDialogDefaultState)

  const drawerExpandDefaultState = {
    do: () => {},
  }
  const [drawerOnExpand, setDrawerOnExpand] = useState(drawerExpandDefaultState)

  const authPopupDefaultState = false
  const [authPopup, setAuthPopup] = useState(authPopupDefaultState)

  const backgroundProcessorDefaultState = {
    tasks: 0,
    add: () => {
      setBackgroundProcessor((prev) => ({ ...prev, tasks: prev.tasks + 1 }))
    },
    remove: () => {
      setBackgroundProcessor((prev) => ({
        ...prev,
        tasks: prev.tasks - 1 <= 0 ? 0 : prev.tasks - 1,
      }))
    },
    reset: () => {
      setBackgroundProcessor((prev) => ({ ...prev, tasks: 0 }))
    },
  }
  const [backgroundProcessor, setBackgroundProcessor] = useState(
    backgroundProcessorDefaultState
  )

  const userDefaultState = null
  const [user, setUser] = useState(userDefaultState)
  const [userGroups, setUserGroups] = useState([])
  const [appMode, setAppMode] = useState(null)

  const appBarTitleDefaultState = ""
  const [appBarTitle, setAppBarTitle] = useState(appBarTitleDefaultState)

  const bottomControlsDefaultState = null
  const [bottomControls, setBottomControls] = useState(bottomControlsDefaultState)

  useEffect(async () => {
    const locationPath = location.pathname
    if (
      locationPath === "/holbaregistration" ||
      locationPath === "/nwecregistration"
    ) {
      amplifyInitHandler()
    } else {
      try {
        Hub.listen("auth", async ({ payload: { event } }) => {
          if (event === "signIn_failure") {
            appResetHandler()

            authOnErrorHandler()
          } else if (event === "oAuthSignOut") {
            appResetHandler()
          } else if (event === "signOut") {
            appResetHandler()

            window.location.reload()
          }
        })

        amplifyInitHandler()

        if (!(await authHasCognitoSessionHandler()))
          throw new Error("No valid session")

        authRedirectHandler()
      } catch (error) {
        if (error.message !== "No valid session") {
          Sentry.captureException(error, { tags: { context: "init" } })
          appResetHandler()

          notificationOnCreateHandler({
            severity: "error",
            message:
              "Apologies, we could not initialize the web application. " +
              "Our team has been notified about the issue. " +
              "Please, wait a moment and try again.",
          })
        }
      }
    }
  }, [])

  useEffect(() => {
    if (notification?.pack.length && !notification?.message) {
      setNotification((prev) => ({
        ...prev,
        message: prev.pack[0],
        open: true,
      }))

      setTimeout(() => {
        notificationOnDestroyHandler()
      }, Number(process.env.REACT_APP_SNACKBAR_DEFAULT_DISPLAY_DURATION))
    }
  }, [notification?.pack, notification?.message])

  useEffect(() => {
    if (authPopup) authPopupOnCloseHandler()
  }, [sm])

  const appResetHandler = () => {
    setNotification(notificationDefaultState)
    setConfirmDialog(confirmDialogDefaultState)
    setMessageDialog(messageDialogDefaultState)
    setDrawerOnExpand(drawerExpandDefaultState)
    setAuthPopup(authPopupDefaultState)
    setBackgroundProcessor(backgroundProcessorDefaultState)
    setUser(userDefaultState)
    setAppBarTitle(appBarTitleDefaultState)
    setBottomControls(bottomControlsDefaultState)

    Sentry.configureScope((scope) => scope.setUser(userDefaultState))
  }

  const amplifyInitHandler = () => {
    const amplifyAuthConfig = {
      identityPoolId: process.env.REACT_APP_AMPLIFY_AUTH_IDENTITY_POOL_ID,
      region: process.env.REACT_APP_AMPLIFY_AUTH_REGION,
      userPoolId: process.env.REACT_APP_AMPLIFY_AUTH_USER_POOL_ID,
      userPoolWebClientId:
        process.env.REACT_APP_AMPLIFY_AUTH_USER_POOL_WEB_CLIENT_ID,
      mandatorySignIn: false,
      oauth: {
        domain: process.env.REACT_APP_AMPLIFY_OAUTH_DOMAIN,
        scope: ["email", "profile", "openid", "aws.cognito.signin.user.admin"],
        redirectSignIn: process.env.REACT_APP_AMPLIFY_OAUTH_REDIRECT_SIGN_IN,
        redirectSignOut: process.env.REACT_APP_AMPLIFY_OAUTH_REDIRECT_SIGN_OUT,
        responseType: "code",
      },
    }

    const amplifyRestApiConfig = [
      {
        name: process.env.REACT_APP_AMPLIFY_API_ENDPOINT_NAME,
        endpoint: process.env.REACT_APP_AMPLIFY_API_ENDPOINT,
        region: process.env.REACT_APP_AMPLIFY_API_ENDPOINT_REGION,
      },
    ]

    const amplifyConfig = {
      aws_project_region: "eu-west-1",
      Auth: amplifyAuthConfig,
      endpoints: amplifyRestApiConfig,
    }

    Amplify.configure(amplifyConfig)
  }

  const authHasCognitoSessionHandler = async () => {
    try {
      const groups = await Auth.currentAuthenticatedUser()

      setUserGroups(groups.signInUserSession.accessToken.payload["cognito:groups"])

      if(groups.signInUserSession.accessToken.payload["cognito:groups"].includes("VeoliaAdmin")){
        setAppMode("Veolia")
      } else if(groups.signInUserSession.accessToken.payload["cognito:groups"].includes("NWECAdmin") ){
        setAppMode("NWEC")
      } else if(groups.signInUserSession.accessToken.payload["cognito:groups"].includes("HOLBAAdmin") ){
        setAppMode("HOLBA")
      }

      const user = await userOnRefreshHandler()

      if (!user) return false

      log("l", "Auth", "Session found")

      Sentry.setUser({
        username: user.attributes.email,
        CognitoUsername: user.username,
        email: user.attributes.email,
        name: user.attributes.name,
      })

      notificationOnCreateHandler({
        severity: "success",
        message: `You are now signed in as ${user.attributes.name}!`,
      })
      if (user) setUser(user)
      return true
    } catch (error) {
      Sentry.captureException(error, { tags: { context: "auth", noFallback: true } })

      return false
    }
  }

  const authRedirectHandler = () => {
    let path = location.pathname

    for (const [key, value] of query.entries()) {
      if (key === "code") break

      if (!path[-1] !== "?") path += "?"
      path += `${key}=${value}`
    }

    history.push(path)
  }

  const authOnErrorHandler = () => {
    notificationOnCreateHandler({
      severity: "error",
      message:
        "Apologies, we could not authenticate you. Our team has been notified about the issue. " +
        "Please, wait a moment and try again.",
    })
  }

  const userOnRefreshHandler = async () => {
    let user = userDefaultState

    try {
      backgroundProcessor?.add()

      user = await Auth.currentUserInfo()

      backgroundProcessor?.remove()
    } catch (error) {
      backgroundProcessor?.remove()

      Sentry.captureException(error, { tags: { context: "user", noFallback: true } })
    }

    if (user) setUser(user)

    return user
  }

  const notificationOnCreateHandler = (notificationProps) => {
    const { severity, message } = notificationProps

    if (
      !severity ||
      typeof severity !== "string" ||
      !notificationSeverityTypes.includes(severity)
    )
      return
    if (!message || typeof message !== "string") return

    setNotification((prev) => ({
      ...prev,
      pack: [...prev.pack, { severity, text: message, key: new Date().getTime() }],
    }))
  }

  const notificationOnDestroyHandler = () => {
    setNotification((prev) => ({
      pack: prev.pack.slice(1),
      message: null,
      open: false,
    }))
  }

  const confirmDialogOnCreateHandler = (confirmDialogProps) => {
    const { title, content, onConfirm, closeOnConfirm } = confirmDialogProps

    if (!title || typeof title !== "string") return
    if (!content || typeof content !== "string") return
    if (!onConfirm || typeof onConfirm !== "function") return
    if (
      closeOnConfirm !== undefined &&
      closeOnConfirm !== null &&
      typeof closeOnConfirm !== "boolean"
    )
      return

    setConfirmDialog({
      open: true,
      title,
      content,
      onConfirm,
      closeOnConfirm:
        closeOnConfirm === undefined || closeOnConfirm === null
          ? true
          : closeOnConfirm,
    })
  }

  const confirmDialogOnDestroyHandler = () => {
    setConfirmDialog((prev) => ({
      ...prev,
      open: false,
      onConfirm: () => {},
    }))
  }

  const messageDialogOnCreateHandler = (messageDialogProps) => {
    const { title, content } = messageDialogProps

    if (!title || typeof title !== "string") return
    if (!content || typeof content !== "string") return

    setMessageDialog({
      open: true,
      title,
      content,
    })
  }

  const messageDialogOnDestroyHandler = () => {
    setMessageDialog((prev) => ({
      ...prev,
      open: false,
    }))
  }

  const authPopupOnToggleHandler = () => {
    setAuthPopup((prev) => !prev)
  }

  const authPopupOnCloseHandler = () => {
    setAuthPopup(false)
  }

  const defaultRouteProps = {
    backgroundProcessor,
    messageDialogCreate: messageDialogOnCreateHandler,
    confirmDialogCreate: confirmDialogOnCreateHandler,
    notificationCreate: notificationOnCreateHandler,
    setAppBarTitle: setAppBarTitle,
    bottomControls: setBottomControls,
    user,
    userGroups,
    location,
  }

  const header = (
    <Fragment>
      <AppBar positionMode="fixed" themeColor="dark" className="k-appbar-header">
        {user && (
          <AppBarSection>
            <Button
              iconClass="fas fa-bars"
              fillMode={'flat'}
              disabled={backgroundProcessor?.tasks}
              onClick={drawerOnExpand?.do}
              themeColor="base"
              style={{paddingTop:"1rem"}}
            />
          </AppBarSection>
        )}

        <AppBarSection>
          {!sm && (
            <img
              style={{
                height: "1.5rem",
                objectFit: "scale-down",
              }}
              src={`${process.env.PUBLIC_URL}/veolia_logo_full_w.png`}
              alt="Veolia full logo"
            />
          )}

          {sm && (
            <img
              style={{
                height: "1.5rem",
                objectFit: "scale-down",
              }}
              src={`${process.env.PUBLIC_URL}/veolia_logo_w.png`}
              alt="Veolia logo"
            />
          )}
        </AppBarSection>

        <AppBarSection style={{ flexGrow: 1 }}>
          <Box
            visibility={backgroundProcessor?.tasks ? "visible" : "hidden"}
            style={{ flexGrow: 1 }}
          >
            <ProgressBar
              value={null}
              labelVisible={false}
              style={{ width: "100%" }}
            />
          </Box>
        </AppBarSection>

        <AppBarSection>
          <h3>{appBarTitle}</h3>
        </AppBarSection>

        {user && !sm && (
          <AppBarSection>
            {user?.attributes.picture && (
              <Avatar type="image" shape="circle" themeColor="base">
                <img
                  src={user?.attributes.picture}
                  alt="User profile"
                  onClick={authPopupOnToggleHandler}
                  ref={authPopupAnchor}
                  className="pointerCursor"
                />
              </Avatar>
            )}

            {!user?.attributes.picture && (
              <div
                onClick={authPopupOnToggleHandler}
                ref={authPopupAnchor}
                className="pointerCursor"
              >
                <Avatar type="text" shape="circle" themeColor="secondary">
                  <span>
                    {user?.attributes.given_name[0]?.toUpperCase()}
                    {user?.attributes.family_name[0]?.toUpperCase()}
                  </span>
                </Avatar>
              </div>
            )}
          </AppBarSection>
        )}
      </AppBar>
    </Fragment>
  )

  const shared = (
    <Fragment>
      <NotificationGroup>
        <Fade enter exit>
          {notification?.open && (
            <Notification
              key={notification?.message ? notification?.message.key : null}
              type={{ style: notification?.message.severity, icon: false }}
              onClose={notificationOnDestroyHandler}
              style={{color: 'white'}}
            >
              {notification?.message ? notification?.message.text : null}
            </Notification>
          )}
        </Fade>
      </NotificationGroup>

      {confirmDialog?.open && (
        <ConfirmDialog
          title={confirmDialog?.title}
          content={confirmDialog?.content}
          confirm={confirmDialog?.onConfirm}
          close={confirmDialogOnDestroyHandler}
          closeOnConfirm={confirmDialog.closeOnConfirm}
        />
      )}

      {messageDialog?.open && (
        <MessageDialog
          title={messageDialog?.title}
          content={messageDialog?.content}
          close={messageDialogOnDestroyHandler}
        />
      )}

      <Popup
        anchor={authPopupAnchor.current}
        show={authPopup}
        popupAlign={{ horizontal: "right", vertical: "top" }}
        anchorAlign={{ horizontal: "right", vertical: "bottom" }}
        animate={false}
        style={{ zIndex: "99999" }}
      >
        <Grid container item direction="column" style={{ padding: "1rem" }}>
          <Grid item>
            <GoogleSignOn {...defaultRouteProps} disableResize={true} dark={false} />
          </Grid>
        </Grid>
      </Popup>
    </Fragment>
  )

  return (
    <Ripple>
      <div
        className="helloWorld"
        style={{ height: "100%" }}
        onClick={() => {
          if (authPopup) authPopupOnCloseHandler()
        }}
      >
        {header}

        {shared}

        <Container maxWidth={false}>
          <DrawerContainer onExpand={setDrawerOnExpand} {...defaultRouteProps}>
            <Switch>
              <Route exact path="/" render={() => <Home {...defaultRouteProps} appMode={appMode} />} />
              <Route
                exact
                path="/holbaregistration"
                render={() => <HolbaRegistration {...defaultRouteProps} />}
              />
              <Route
                exact
                path="/nwecregistration"
                render={() => <NwecRegistration {...defaultRouteProps} />}
              />
              <Route
                exact
                path="/manage"
                render={() =>
                  user ? <ManageUsers {...defaultRouteProps} appMode={appMode} /> : <Redirect to="/" />
                }
              />
              <Route
                exact
                path="/nominateofficers"
                render={() =>
                  user ? <NominateOfficers {...defaultRouteProps} appMode={appMode} /> : <Redirect to="/" />
                }
              />
              <Route
                exact
                path="/sendrequest"
                render={() =>
                  user ? <SendRequest {...defaultRouteProps} appMode={appMode} /> : <Redirect to="/" />
                }
              />
              <Route
                exact
                path="/not-found"
                render={() => <NotFound {...defaultRouteProps} />}
              />
              <Route
                exact
                path="/error/:id"
                render={() => <ErrorFallback {...defaultRouteProps} />}
              />
              <Route
                exact
                path="/maintenance"
                key={Date.now()}
                render={() => <MaintenanceFallback {...defaultRouteProps} />}
              />
              <Route
                exact
                path="/debug"
                render={() =>
                  user ? <Debug {...defaultRouteProps} /> : <Redirect to="/" />
                }
              />
              <Route
                exact
                path="/feedback"
                render={() =>
                  user ? <Feedback {...defaultRouteProps} /> : <Redirect to="/" />
                }
              />

              <Route exact path="*" render={() => <Redirect to="/not-found" />} />
            </Switch>
          </DrawerContainer>
        </Container>

        {bottomControls && <Fragment>{bottomControls}</Fragment>}
      </div>
    </Ripple>
  )
}

export default App
